import { Dtos, formatAddress } from "@inrev/common";
import { FC, useId, useState } from "react";
import { formatName } from "../../../utils";
import { CopyableText } from "../../patterns/copyable-text";
import { PartialIndicator } from "../../primitives";
import { ClickableIcon } from "../../primitives/ClickableIcon";
import { Icon } from "../../primitives/Icon";
import { PortaledTooltip } from "../../ui/Tooltip";
import { TruncatedLabel } from "../../ui/TruncatedLabel";
import { DetailCard } from "./DetailCard";
import { DetailStatusCell, type DetailStatusCellProps } from "./DetailStatusCell";
import { useDeleteContactConfirmation } from "./useDeleteContactConfirmation";

type Individual = Dtos.Individual.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Individual;

export type IndividualContactCardProps = {
	individual: Individual;
	spouseName?: string;
	statusCells: DetailStatusCellProps[];
	onEdit?: () => void;
	onDelete?: () => void;
	deleteSubMessage?: string;
	partialActive?: boolean;
};

export const IndividualContactCard: FC<IndividualContactCardProps> = ({
	individual,
	spouseName,
	statusCells,
	onEdit,
	onDelete,
	deleteSubMessage,
	partialActive = false,
}) => {
	const { ownsAHome, email, ssn } = individual;
	const name = formatName(individual) ?? "";
	const address = individual.address ? formatAddress(individual.address) : undefined;
	const [revealSsn, setRevealSsn] = useState(false);
	const spouseTooltipId = useId();
	const { requestDelete, confirmationModal } = useDeleteContactConfirmation(
		name,
		onDelete,
		deleteSubMessage,
	);

	return (
		<>
			<DetailCard
				onEdit={onEdit}
				onDelete={requestDelete}
				title={
					<div className="flex items-center space-x-[8px] min-w-0">
						<TruncatedLabel
							mask
							label={name}
							className="text-[15px] font-medium text-primary min-w-0"
						/>
						{spouseName && (
							<>
								<span
									data-tooltip-id={spouseTooltipId}
									className="shrink-0 flex items-center cursor-default"
								>
									<Icon type="diamond-ring" size="mini" color="tertiary" />
								</span>
								<PortaledTooltip id={spouseTooltipId} content={`Spouse: ${spouseName}`} />
							</>
						)}
					</div>
				}
			>
				<div className="flex flex-col mt-[6px] space-y-[4px] text-[14px] text-secondary min-w-0">
					<div className="flex items-center space-x-[8px] min-w-0">
						<Icon
							type={ownsAHome ? "home" : "map-pin"}
							size="mini"
							color="tertiary"
							className="stroke-[1.75] shrink-0"
						/>
						{address ? (
							<>
								<CopyableText mask value={address} />
								{partialActive && individual.type === "owner" && ownsAHome === undefined && (
									<PartialIndicator active />
								)}
							</>
						) : (
							<>
								<span className="text-tertiary">--</span>
								{partialActive && <PartialIndicator active />}
							</>
						)}
					</div>
					<div className="flex items-center space-x-[8px] min-w-0">
						<Icon
							type="at-symbol"
							size="mini"
							color="tertiary"
							filled
							className="stroke-[0.5] shrink-0"
						/>
						{email ? (
							<CopyableText mask value={email} />
						) : (
							<span className="text-tertiary">--</span>
						)}
					</div>
					<div className="flex items-center space-x-[8px] min-w-0">
						<span className="w-[16px] text-[9px] font-medium text-tertiary italic shrink-0">
							SSN
						</span>
						{ssn ? (
							<>
								<CopyableText
									mask
									value={revealSsn ? ssn : ssn.replace(/./g, "*")}
									disableCopy={!revealSsn}
									className={revealSsn ? undefined : "translate-y-[3px]"}
								/>
								<ClickableIcon
									data-testid="reveal-ssn"
									tooltip="Toggle SSN visibility"
									type={revealSsn ? "eye-slash" : "eye"}
									color="tertiary"
									size="mini"
									className="shrink-0"
									onClick={() => setRevealSsn((prev) => !prev)}
								/>
							</>
						) : (
							<>
								<span className="text-tertiary">--</span>
								{partialActive && individual.type === "owner" && <PartialIndicator active />}
							</>
						)}
					</div>
				</div>
				<div className="flex items-center justify-between mt-[4px] pt-[4px]">
					{statusCells.map((cell) => (
						<DetailStatusCell key={cell.label} {...cell} />
					))}
				</div>
			</DetailCard>
			{confirmationModal}
		</>
	);
};
